/**
 * inyaneiAnnotations.js
 *
 * Maps halachic items from buildHalachicItems() to time-based annotation items
 * that can be injected into the ZmanimWidget timeline.
 *
 * Each annotation is a lightweight timeline item that appears near the
 * relevant zman on the timeline (e.g., hallel near sunrise, musaf near chatzot).
 */

import { ScrollText } from 'lucide-react';

// ─── Category → zman key mapping ───────────────────────────────────────────
// Each category is associated with a zman key. The annotation will appear
// at the time of that zman on the timeline.

const CATEGORY_ZMAN_MAP = {
  selichot:         'alotHaShachar',  // Selichot before dawn
  tachanun:         'sunrise',        // Part of Shacharit
  hallel:           'sunrise',        // After Shacharit Amidah
  prayerAdditions:  'sunrise',        // Amidah insert
  seasonalPrayers:  'sunrise',        // Amidah seasonal
  weekdayPrayers:   'sunrise',        // Avinu Malkeinu etc.
  shabbatAdditions: 'sunrise',        // Shabbat specific
  liturgy:          'chatzot',        // Musaf, Yizkor around midday
  mourningPeriod:   'sunrise',        // General day status
  seasonalCustoms:  'sunset',         // Omer, Eruv Tavshilin at evening
  motzaeiShabbat:   'tzeit85deg',     // Post-Shabbat
  leyning:          'sunrise',        // Torah reading in the morning
};

// Specific overrides for certain labels
const LABEL_ZMAN_OVERRIDES = {
  'ספירת העומר':    'sunset',
  'עירוב תבשילין':  'sunset',
  'קידוש לבנה':     'tzeit85deg',
  'מוסף':           'chatzot',
  'יזכור':          'chatzot',
};

const ANNOTATION_ACCENT = '#4a9d8e'; // Teal — distinct from prayer (navy) and moad (amber)

/**
 * Convert halachic items to timeline annotation items.
 * Items are grouped by their target zman so each zman gets a single
 * compact annotation block instead of many individual items.
 *
 * @param {Array} inyaneiItems — items from buildHalachicItems()
 * @param {object} zmanimData  — raw zmanim object { sunrise: 'HH:MM', ... }
 * @returns {Array} annotation items for customScheduleItems
 */
export function mapToAnnotations(inyaneiItems, zmanimData) {
  if (!inyaneiItems?.length || !zmanimData) return [];

  // Group items by their target zman key
  const groups = {};

  for (const item of inyaneiItems) {
    const zmanKey =
      LABEL_ZMAN_OVERRIDES[item.label] ||
      CATEGORY_ZMAN_MAP[item.category] ||
      'sunrise';

    const time = zmanimData[zmanKey];
    if (!time || time === '--:--') continue;

    if (!groups[zmanKey]) {
      groups[zmanKey] = { time, items: [] };
    }
    groups[zmanKey].items.push(item);
  }

  return Object.entries(groups).map(([, group]) => ({
    time: offsetMinutes(group.time, -1),
    title: group.items.map(i => i.value || i.label).join(' · '),
    items: group.items,
    type: 'annotation',
    accentColor: ANNOTATION_ACCENT,
    icon: ScrollText,
    isAnnotation: true,
  }));
}

/**
 * Offset a time string by N minutes.
 * @param {string} timeStr — 'HH:MM'
 * @param {number} minutes — offset (can be negative)
 * @returns {string} 'HH:MM'
 */
function offsetMinutes(timeStr, minutes) {
  const [h, m] = timeStr.split(':').map(Number);
  let total = h * 60 + m + minutes;
  total = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
